import React, {
  useState,
  ReactNode,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import cn from 'classnames';
import styles from './Tooltip.module.css';

export type TooltipTrigger = 'hover' | 'click';
export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  trigger?: TooltipTrigger;
  position?: TooltipPosition;
  className?: string;
  tooltipClassName?: string;
  disabled?: boolean;
  delay?: number; // Delay in ms for hover trigger
  isVisible?: boolean; // Controlled visibility
  onVisibilityChange?: (isVisible: boolean) => void;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  trigger = 'hover',
  position = 'top',
  className,
  tooltipClassName,
  disabled = false,
  delay = 100,
  isVisible: controlledIsVisible,
  onVisibilityChange,
}) => {
  const [uncontrolledIsVisible, setUncontrolledIsVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isVisible =
    controlledIsVisible !== undefined
      ? controlledIsVisible
      : uncontrolledIsVisible;

  const showTooltip = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(
      () => {
        if (onVisibilityChange) {
          onVisibilityChange(true);
        } else {
          setUncontrolledIsVisible(true);
        }
      },
      trigger === 'hover' ? delay : 0,
    );
  }, [delay, onVisibilityChange, trigger]);

  const hideTooltip = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (onVisibilityChange) {
      onVisibilityChange(false);
    } else {
      setUncontrolledIsVisible(false);
    }
  }, [onVisibilityChange]);

  const toggleTooltip = useCallback(() => {
    if (onVisibilityChange) {
      onVisibilityChange(!isVisible);
    } else {
      setUncontrolledIsVisible(prev => !prev);
    }
  }, [isVisible, onVisibilityChange]);

  useEffect(() => {
    if (trigger !== 'click' || !isVisible) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        hideTooltip();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, trigger, hideTooltip, onVisibilityChange]);

  if (disabled) {
    return <>{children}</>;
  }

  const triggerProps = {
    onMouseEnter: trigger === 'hover' ? showTooltip : undefined,
    onMouseLeave: trigger === 'hover' ? hideTooltip : undefined,
    onClick: trigger === 'click' ? toggleTooltip : undefined,
    ref: triggerRef,
    className: styles.triggerWrapper,
  };

  return (
    <div className={cn(styles.tooltipContainer, className)}>
      <div {...triggerProps}>{children}</div>
      {isVisible && content && (
        <div
          ref={tooltipRef}
          className={cn(styles.tooltip, styles[position], tooltipClassName)}
          role='tooltip'
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
